import React, { Fragment, useState, useEffect, useRef } from "react"

import UserSessionForm from "../UserSessionForm/UserSessionForm"
import PlaceOrderButton from "./components/forms/PlaceOrderButton"
import DeliveryAddressForm from "./components/forms/DeliveryAddressForm"
import DeliveryTimeSlotForm from "./components/forms/DeliveryTimeSlotForm"
import TakeAwayTimeSlotForm from "./components/forms/TakeAwayTimeSlotForm"
import PaymentDetailsForm from "./components/forms/payment/PaymentDetailsForm"
import BillingDetailsForm from "./components/forms/BillingDetailsForm"
import PhoneDetailsForm from "./components/forms/PhoneDetailsForm"
import OrderPreferencesForm from "./components/forms/OrderPreferencesForm"
import CurrentUserStore from "../stores/CurrentUserStore"
import FluxAuthenticationActions from "../actions/FluxAuthenticationActions"
import AlertModal from "../ui-components/AlertModal"
import ServicesCart from "../CartContainer/components/ServicesCart"

import CartStateStore, {
  CHANGE_STORE,
  getCart,
  PLACE_ORDER,
  RESET_PAYMENT_INFO,
  UPDATE_DELIVERY_DETAILS,
  UPDATE_PAYMENT_INFO,
  useCartStateStore,
} from "../stores/CartStateStore"
import getTarget from "../utils/getTarget"
import BrowserLocationHelper from "../utils/browser_location_helper"
import { getFromLocalStorage } from "../utils/localStorage"
import { getTakeAwaySlots } from "../apis/TakeAwaySlots"
import { ORDER_MAX_CHANGE_THRESHOLD } from "../utils/config"

import { CreateOrder } from "../apis/OrdersAPI"

import style from "./style.module"

function _CheckoutApp(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    user_session_token,
    current_user,
    venue,
    delivery_address,
    deliverySlots,
    createOrderAPIURL,
    stripe_key,
    available_coupons,
    venue_accepts_stripe,
    venue_accepts_cash,
    show_stripe_method,
    billing_details,
  } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [state, setState] = useState({
    current_user: current_user,
    disabled: false,
    errors: {},
    errorModalText: null,
    timeSlotIndex: null,
    user_session_token: user_session_token,
    stripe_payment_intent_id: null,
    isTakeAway: false,
    takeAwayTimeSlots: null,
    selectedPickingTime: null,
    takeAwayTimeSlot: "",
    verifiedCountryPrefix: "",
    verifiedPhoneNumber: "",
  })
  const disableTimer = useRef(null)
  const [cartState, dispatchCartState] = useCartStateStore()

  const [deliveryAddressState, setDeliveryAddressState] = useState({
    street_name: delivery_address.street_name || "",
    street_number: delivery_address.street_number || "",
    city: delivery_address.city || "",
    postal_code: delivery_address.postal_code || "",
    latitude: delivery_address.latitude || "",
    longitude: delivery_address.longitude,
    phone: delivery_address.phone || "",
    phone_confirmation: delivery_address.phone_confirmation || "",
    country_prefix: delivery_address.country_prefix || "+39",
    country_code: delivery_address.country_code || "it",
    delivery_notes: delivery_address.delivery_notes || "",
    restricted_traffic_area: delivery_address.restricted_traffic_area === true,
  }) // TODO: refactor default state

  const [phoneDetailsState, setPhoneDetailsState] = useState({
    isNumberLoaded: false,
    needVerification: false,
    needToModify: false,
    number: "",
    newNumber: "",
    code: "",
    countryCode: "IT",
    countryPrefix: "+39",
    isVerified: false,
    recaptchaVerifying: false,
    error: {
      ok: true,
      type: "",
      message: "",
    },
    isSendingSms: false,
    isConfirmingCode: false,
  }) // TODO: refactor default state

  const [billingDetailsState, setBillingDetailsState] = useState({
    business_name: billing_details.business_name,
    business_address: billing_details.business_address,
    business_invoice_code: billing_details.business_invoice_code,
    vat_code: billing_details.vat_code,
    tax_code: billing_details.tax_code,
    request_invoice: billing_details.request_invoice,
  }) // TODO: refactor default state

  const [OrderPreferencesState, setOrderPreferencesState] = useState({
    baby_sleeping: false,
    order_modifications: "",
  })

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    CurrentUserStore.addChangeListener(handleCurrentUserStoreChange)

    initializeTakeAwaySlots()

    dispatchCartState({ type: CHANGE_STORE, payload: venue })

    return () => {
      CurrentUserStore.removeChangeListener(handleCurrentUserStoreChange)

      clearInterval(disableTimer.current)
    }
  }, [])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function handleCurrentUserStoreChange() {
    const user = CurrentUserStore.getUser()

    // @todo: remove this hack. If a user is not logged in we should redirect to the sign_in page instead of the checkout app
    // and when the user signs in so we should redirect to the CheckoutApp
    // this could simplify the logic in Checkout app
    // this workaround is here to let the navbar refresh and display the state correctly once the user is logged in
    BrowserLocationHelper.ReloadPage()

    setState({
      ...state,
      current_user: user,
      user_session_token:
        user && user.user_session ? user.user_session.token : null,
    })
  }

  async function initializeTakeAwaySlots() {
    // If is a takeaway order get takeaway slots and add them to the state
    if (JSON.parse(getFromLocalStorage("take_away")) === true) {
      const { latitude, longitude } = delivery_address
      const venueId = venue && venue.id
      const time = new Date()
      const takeAwaySlots = await getTakeAwaySlots(
        venueId,
        latitude,
        longitude,
        time
      )
      setState({ ...state, isTakeAway: true, takeAwayTimeSlots: takeAwaySlots })
    }
  }

  function handleInputChange(event) {
    setState({ ...state, [event.target.name]: event.target.value })
  }

  function handleTimeSlotIndexChanged(index) {
    if (!index) return

    const slots = deliverySlots

    const slot = slots[index]

    dispatchCartState({
      type: UPDATE_DELIVERY_DETAILS,
      payload: { price: slot?.price_cents || 0, errors: [] },
    })
    setState({ ...state, timeSlotIndex: index })
  }

  function handleTakeAwayTimeSlotChange(e) {
    const target = getTarget(e)
    setState({
      ...state,
      selectedPickingTime: target.value,
      takeAwayTimeSlot: target.options[target.selectedIndex].dataset.timeSlot,
    })
  }

  function getSelectedStartTime() {
    const deliverySlot = getSelectedTimeSlot()

    return deliverySlot ? deliverySlot.start_time : null
  }

  function getSelectedTimeSlot() {
    const index = state.timeSlotIndex
    const slots = deliverySlots

    return index && slots[index]
  }

  function getUserSessionToken() {
    return state.user_session_token
  }

  function getOrderHash() {
    const isTakeAway = state.isTakeAway
    const delivery_address = deliveryAddressState
    const billing_details = billingDetailsState
    const phoneDetailsForm = phoneDetailsState
    const deliverySlot = getSelectedTimeSlot()
    const payment_details = cartState.paymentMethod
    const order_preferences = OrderPreferencesState
    const coupon_code = Object.keys(getCart(cartState).coupons || {})[0]
    const timeSlot = deliverySlot
      ? `${deliverySlot.start_time}-${deliverySlot.end_time}`
      : isTakeAway
      ? state.takeAwayTimeSlot
      : null
    const proposed_picking_time = isTakeAway ? state.selectedPickingTime : null
    const vehicleType = deliverySlot ? deliverySlot.vehicle_type : null
    const orderType =
      deliverySlot && deliverySlot.vehicle_type === "venue"
        ? "venue"
        : isTakeAway
        ? "take_away"
        : null

    let order = {
      phone_attributes: {
        country_code: phoneDetailsForm.countryCode,
        country_prefix: phoneDetailsForm.countryPrefix,
        number: phoneDetailsForm.number,
        verified: phoneDetailsForm.isVerified,
      },
      latitude: delivery_address.latitude,
      longitude: delivery_address.longitude,
      street_name: delivery_address.street_name,
      street_number: delivery_address.street_number,
      postal_code: delivery_address.postal_code,
      city: delivery_address.city,
      delivery_notes: delivery_address.delivery_notes,
      restricted_traffic_area: delivery_address.restricted_traffic_area,
      baby_sleeping: order_preferences && order_preferences.baby_sleeping,
      order_modifications:
        order_preferences && order_preferences.order_modifications,
      line_items: getCart(cartState).products,
      venue_id: venue.id,
      time_slot: timeSlot,
      proposed_picking_time: proposed_picking_time,
      vehicle_type: vehicleType,
      coupon_code: coupon_code,
      payment_method: payment_details.type,
      request_invoice: billing_details.request_invoice,
      locale: I18n.locale,
      metadata: { source: "website" },
    }

    if (billing_details.request_invoice) {
      order.business_name = billing_details.business_name
      order.business_address = billing_details.business_address
      order.business_invoice_code = billing_details.business_invoice_code
      order.vat_code = billing_details.vat_code
      order.tax_code = billing_details.tax_code
    }

    switch (payment_details.type) {
      case "cash":
        order.cash_amount =
          parseFloat(payment_details.options.cash_amount).toFixed(2) || 0
        break
      case "stripe":
        order.payment_attributes = {
          payment_method: payment_details.options.method,
          flow_mode: "async",
        }
        break
      case "satispay":
        order.payment_attributes = {
          flow_mode: "async",
        }
        break
    }

    return { order: order, order_type: orderType }
  }

  function isOrderFormValid() {
    const phoneDetails = phoneDetailsState

    const payment_details = cartState.paymentMethod

    if (!phoneDetails || !phoneDetails.isVerified) {
      setState({
        ...state,
        errorModalText: I18n.t("cart_summary.phone_required_verified"),
      })
      return false
    }

    if (!deliveryAddressState.street_number && !state.isTakeAway) {
      setState({
        ...state,
        errorModalText: I18n.t("cart_summary.street_number_alert"),
      })
      return false
    }

    // ADD TakeAwaySlot validation

    // TODO: Move validations away from here
    switch (payment_details.type) {
      case "cash":
        const cash_amount = payment_details.options.cash_amount

        if (state.isTakeAway === true) {
          break
        }

        if (!cash_amount || cash_amount === "") {
          setState({
            ...state,
            errorModalText: I18n.t("cart_summary.cash_value_alert"),
          })
          return false
        } else {
          // check amount > cartTotal
          const amount = parseFloat(cash_amount).toFixed(2) * 100 || 0
          let discountedTotal = getCart(cartState).total
          if (amount < discountedTotal) {
            setState({
              ...state,
              errorModalText: I18n.t("cart_summary.cash_value_too_low_alert"),
            })
            return false
          }
          // check amount - cart total < threshold
          let deliveryPrice = cartState.deliveryDetails?.price || 0
          let order_max_change = ORDER_MAX_CHANGE_THRESHOLD * 100
          if (amount - discountedTotal - deliveryPrice > order_max_change) {
            let maxAmount = discountedTotal + deliveryPrice + order_max_change
            setState({
              ...state,
              errorModalText: I18n.t("cart_summary.change_too_high_alert", {
                max_value: (maxAmount / 100.0).toFixed(2),
                change_value: ORDER_MAX_CHANGE_THRESHOLD,
              }),
            })
            return false
          }
        }

        break

      case "stripe":
        if (!payment_details.options.method) {
          setState({
            ...state,
            errorModalText: "Devi aggiungere o scegliere una carta di credito.",
          })
          return false
        }
        break
      case "satispay":
        // No validation needed
        break

      default:
        setState({ ...state, errorModalText: "Metodo di pagamento non valido" })
        return false
    }
    return true
  }

  async function placeOrder(e) {
    e.preventDefault && e.preventDefault()

    if (!isOrderFormValid()) {
      return
    }

    try {
      dispatchCartState({
        type: UPDATE_PAYMENT_INFO,
        payload: { createdAt: new Date().toISOString() },
      })
      setState({ ...state, disabled: true })

      const order = await CreateOrder(getOrderHash(), getUserSessionToken())

      // order is created, async payment is now processing
      handleOrderCreated(order)
    } catch (error) {
      // remove the payment in progress. The user is aware that something went wrong
      handleOrderCreationError(error)
    }
  }

  function handleOrderCreated(order) {
    if (order?.payment?.method_name === "cash") {
      // 'cash' has no more steps, the order is fine -> cleanup
      dispatchCartState({
        type: PLACE_ORDER,
        payload: { venue_id: order.venue.id, order: order },
      })
    }

    // Wait 2 seconds to be sure the call to external analytics services
    setTimeout(function () {
      BrowserLocationHelper.ChangeURL("/" + I18n.locale + "/orders/" + order.id)
    }, 2000)
  }

  function handleOrderCreationError(error) {
    dispatchCartState({
      type: RESET_PAYMENT_INFO,
      payload: {},
    })

    setState({ ...state, disabled: false })

    if (error.status === 401) {
      FluxAuthenticationActions.unauthorized()
      return
    }

    const {
      response: { body },
    } = error

    if (body) {
      const { full_error_messages, detailed_errors } = body
      let message = I18n.t("cart_summary.missing_fields_alert")

      if (full_error_messages && full_error_messages.length > 0) {
        message = full_error_messages.join(", ")
      }

      setState({
        ...state,
        errorModalText: message,
        errors: detailed_errors,
        disabled: false,
      })
    } else {
      this.setState({
        ...state,
        errorModalText: "Errore",
        errors: "Errore generico",
        disabled: false,
      })
    }
  }

  function isCurrentUserLogged() {
    return state.current_user !== null
  }

  function renderCheckoutForm() {
    return (
      <form action="#" className="" onSubmit={placeOrder}>
        <DeliveryAddressForm
          state={deliveryAddressState}
          setState={setDeliveryAddressState}
          venue={venue}
          isTakeAway={state.isTakeAway}
          errors={state.errors}
        />
        <PhoneDetailsForm
          state={phoneDetailsState}
          setState={setPhoneDetailsState}
          userToken={state.user_session_token}
          errors={state.errors}
        />
        {state.isTakeAway ? (
          <TakeAwayTimeSlotForm
            errors={state.errors}
            onChange={handleTakeAwayTimeSlotChange}
            selectedPickingTime={state.selectedPickingTime}
            takeAwaySlots={state.takeAwayTimeSlots}
          />
        ) : (
          <DeliveryTimeSlotForm
            errors={state.errors}
            onChange={handleTimeSlotIndexChanged}
            slotIndex={state.timeSlotIndex}
            slots={deliverySlots}
          />
        )}

        <PaymentDetailsForm
          userAuthToken={getUserSessionToken()}
          acceptsCoupons={venue.accepts_discount_codes}
          availableCoupons={available_coupons}
          showAlertMessage={!state.isTakeAway}
          venueId={venue.id}
          startTime={getSelectedStartTime()}
          currency="EUR"
          isTakeAway={state.isTakeAway}
          errors={state.errors}
          venueAcceptsStripe={venue_accepts_stripe}
          venueAcceptsCash={venue_accepts_cash}
          showStripeMethod={show_stripe_method}
          amount={getCart(cartState).total}
          deliveryPrice={cartState.deliveryDetails?.price || 0}
          cartState={cartState}
          dispatchCartState={dispatchCartState}
          stripe_key={stripe_key}
        />

        <BillingDetailsForm
          state={billingDetailsState}
          setState={setBillingDetailsState}
          errors={state.errors}
        />
        {!state.isTakeAway && (
          <OrderPreferencesForm
            state={OrderPreferencesState}
            setState={setOrderPreferencesState}
          />
        )}
        <PlaceOrderButton waiting={state.disabled} onClick={placeOrder} />
      </form>
    )
  }

  function renderUserSessionForm() {
    return (
      <div className="order-summary order-step">
        <UserSessionForm showSignIn={true} />
      </div>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  // -------------------------------------

  return (
    <Fragment>
      <div className="col-sm-8">
        <h2>{I18n.t("pages.orders.new.title")}</h2>
        <div className="order-details">
          <div className={style.container}>
            <AlertModal
              title={I18n.t("pages.orders.new.warning")}
              text={state.errorModalText}
              show={!!state.errorModalText}
              onClose={() => setState({ ...state, errorModalText: null })}
            />

            {isCurrentUserLogged()
              ? renderCheckoutForm()
              : renderUserSessionForm()}
          </div>
        </div>
      </div>
      <div className="col-sm-4">
        <h2>{I18n.t("pages.orders.new.title_summary")}</h2>
        <div className="order-summary order-step">
          <ServicesCart
            editable={false}
            cart={getCart(cartState)}
            minDeliveryPrice={cartState.deliveryDetails?.price || 0}
            maxDeliveryPrice={cartState.deliveryDetails?.price || 0}
            isTakeawayActive={state.isTakeAway}
          />
        </div>
      </div>
    </Fragment>
  )
}

export default function CheckoutApp(props) {
  return (
    <CartStateStore immutable={true}>
      <_CheckoutApp {...props}></_CheckoutApp>
    </CartStateStore>
  )
}
